import { XMarkIcon } from "@heroicons/react/24/outline"
import { useState } from "react"
import { createPortal } from "react-dom"

import { Icon } from "@/common/icons/Icon"

import { MaintenanceSeverity, useMaintenanceMode } from "./useMaintenanceMode"

type SeverityStyles = {
  topBorder: string
  bubble: string
  icon: string
  iconBg: string
  text: string
  textMuted: string
  link: string
  progress: string
}

const severityConfig: Record<MaintenanceSeverity, SeverityStyles> = {
  info: {
    topBorder: "border-t-blue-500",
    bubble: "bg-blue-500 hover:bg-blue-600",
    icon: "text-blue-600",
    iconBg: "bg-blue-50",
    text: "text-gray-900",
    textMuted: "text-gray-600",
    link: "text-blue-600 hover:text-blue-700",
    progress: "bg-blue-500"
  },
  warning: {
    topBorder: "border-t-amber-500",
    bubble: "bg-amber-500 hover:bg-amber-600",
    icon: "text-amber-600",
    iconBg: "bg-amber-50",
    text: "text-gray-900",
    textMuted: "text-gray-600",
    link: "text-amber-600 hover:text-amber-700",
    progress: "bg-amber-500"
  },
  critical: {
    topBorder: "border-t-red-500",
    bubble: "bg-red-500 hover:bg-red-600",
    icon: "text-red-600",
    iconBg: "bg-red-50",
    text: "text-gray-900",
    textMuted: "text-gray-600",
    link: "text-red-600 hover:text-red-700",
    progress: "bg-red-500"
  }
}

/**
 * Maintenance banner component for production deployments.
 *
 * Displays a user-friendly notification during maintenance windows with:
 * - Warm, professional messaging that reduces user anxiety
 * - Live countdown timer and progress bar (capped at 90%)
 * - Collapsible to a small bubble or completely dismissible
 * - Three severity levels (info, warning, critical) with appropriate styling
 * - Optional status page link
 *
 * Configuration via environment variables (see useMaintenanceMode hook).
 *
 * @example
 * ```tsx
 * // In _app.tsx
 * <MaintenanceBanner />
 * ```
 *
 * @example
 * ```bash
 * # Enable maintenance mode
 * VITE_MAINTENANCE_MODE=true
 * VITE_MAINTENANCE_SEVERITY=warning
 * VITE_MAINTENANCE_MINUTES=120
 * VITE_MAINTENANCE_STARTED_AT=2025-01-15T02:00:00Z
 * VITE_MAINTENANCE_DEFAULT_STATE=opened
 * VITE_MAINTENANCE_DESTROY_ON_DISMISS=false
 * ```
 */
export function MaintenanceBanner() {
  const config = useMaintenanceMode()
  const [isExpanded, setIsExpanded] = useState(config.defaultState === "opened")
  const [isDismissed, setIsDismissed] = useState(false)

  if (!config.isActive || isDismissed) {
    return null
  }

  const styles = severityConfig[config.severity]

  const handleDismiss = () => {
    if (config.destroyOnDismiss) {
      setIsDismissed(true)
    } else {
      setIsExpanded(false)
    }
  }

  const minutesLeft = config.minutesRemaining ?? config.estimatedMinutes
  const showTimer = minutesLeft >= 60
  const timeDisplay = showTimer
    ? `~${Math.ceil(minutesLeft / 60)}h remaining`
    : "Wrapping up soon..."
  const timerLabel = showTimer ? "Time remaining" : null

  const cappedProgress =
    config.progressPercent === null ? null : Math.min(90, config.progressPercent)

  const defaultTitle =
    config.severity === "critical"
      ? "Critical Maintenance in Progress"
      : "We've got something special in store for you!"

  const defaultMessage =
    config.severity === "critical"
      ? "Please avoid making changes until we're done."
      : "And we can't wait for you to see it. Please check back soon."

  const additionalInfo =
    config.severity === "critical"
      ? "Any changes made during this time may be lost."
      : "We recommend read-only mode during this window. In the rare case we need to restore from backup, recent edits could be affected."

  const title = defaultTitle
  const message = config.customMessage || defaultMessage

  if (typeof document === "undefined") {
    return null
  }

  if (!isExpanded) {
    return createPortal(
      <button
        onClick={() => setIsExpanded(true)}
        className={`rounded-nx-full fixed top-2 left-1/2 -translate-x-1/2 p-2 shadow-lg ${styles.bubble} animate-pulse text-white transition-all duration-200 hover:scale-110`}
        aria-label="Show maintenance notice"
        title="Maintenance in progress - click to view details"
      >
        <Icon name="triangle-exclamation" size={16} />
      </button>,
      document.body
    )
  }

  // Expanded compact notification view
  return createPortal(
    <div className="fixed inset-x-0 top-4 flex justify-center px-4" role="alert" aria-live="polite">
      <div
        className={`bg-nx-surface-bg-base w-full max-w-lg ${styles.topBorder} animate-in fade-in-0 zoom-in-95 rounded-nx-lg border-nx-stroke-default border border-t-2 shadow-lg duration-300`}
      >
        <div className="flex items-start gap-3 px-3 pt-3 pb-3.5">
          <div className={`${styles.iconBg} rounded-nx-md mt-0.5 shrink-0 p-2`}>
            <Icon name="triangle-exclamation" size={20} className={styles.icon} />
          </div>

          <div className="min-w-0 flex-1">
            <div className="flex items-center justify-between gap-2">
              <h3 className={`font-semibold ${styles.text}`}>{title}</h3>
              <button
                type="button"
                onClick={handleDismiss}
                className="rounded-nx-md hover:bg-nx-surface-bg-hover shrink-0 p-1 text-gray-400 hover:text-gray-500"
                aria-label="Minimize"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>

            <p className={`text-sm ${styles.textMuted} mt-0.5`}>{message}</p>

            <p className={`text-xs ${styles.textMuted} mt-1 opacity-80`}>{additionalInfo}</p>

            <div className="mt-2 space-y-1.5">
              <div className="flex items-center justify-between gap-3">
                <div className={`text-xs ${styles.textMuted} flex items-center gap-1.5`}>
                  {timerLabel && (
                    <>
                      <span className="material-symbols-rounded text-sm">schedule</span>
                      <span className="font-medium">{timerLabel}:</span>
                    </>
                  )}
                  <span>{timeDisplay}</span>
                </div>

                {config.statusPageUrl && (
                  <a
                    href={config.statusPageUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`inline-flex items-center gap-0.5 text-xs ${styles.link} hover:underline`}
                  >
                    <span>Status</span>
                    <span className="material-symbols-rounded text-xs">open_in_new</span>
                  </a>
                )}
              </div>

              {cappedProgress !== null && cappedProgress > 0 && (
                <div className="w-full">
                  <div className="rounded-nx-full h-1 overflow-hidden bg-black/10">
                    <div
                      className={`h-full ${styles.progress} transition-all duration-500`}
                      style={{ width: `${cappedProgress}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body
  )
}

MaintenanceBanner.displayName = "MaintenanceBanner"
